import {
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  Sheet,
  Typography,
} from "@mui/joy";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useCurrentConversationStore from "../stores/currentConversationStore";
import { CharacterAvatar } from "./CharacterAvatar";
import { closeSidebar } from "./sidebar";

interface Chat {
  character_id: string;
  character_name: string;
  created_at: string;
  id: string;
  last_message: string;
  avatar_url: string;
  last_message_time: string;
}

interface ChatsResponse {
  chats: Chat[];
}

interface GroupedChats {
  [key: string]: Chat[];
}

export function Chats(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const setConversation = useCurrentConversationStore(
    (state) => state.setConversation,
  );
  const { data, isLoading, error } = useQuery<ChatsResponse>({
    queryKey: ["chats"],
    queryFn: async () => {
      const response = await fetch("/api/chats");
      if (!response.ok) {
        throw new Error("Failed to fetch chats");
      }
      const data = await response.json();
      data.chats = data.chats.slice(0, 30);
      return data;
    },
  });

  if (isLoading) {
    return (
      <Sheet
        sx={{
          display: "flex",
          justifyContent: "left",
          alignItems: "center",
          height: "100%",
          p: 2,
          backgroundColor: "transparent",
        }}
      >
        <Typography level="body-sm">{t("chats.loading")}</Typography>
      </Sheet>
    );
  }

  if (error) {
    return (
      <Sheet
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          p: 2,
          backgroundColor: "transparent",
        }}
      >
        <Typography level="body-sm" color="danger">
          {t("chats.errorLoading")}
        </Typography>
      </Sheet>
    );
  }

  if (!data?.chats?.length) {
    return (
      <Sheet
        sx={{
          display: "flex",
          bgcolor: "transparent",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          p: 2,
        }}
      >
        <Typography
          level="body-xs"
          sx={{
            color: "neutral.500",
            textAlign: "center",
            bgcolor: "transparent",
            fontSize: "0.875rem",
          }}
        >
          {t("chats.noChatsYet")}
        </Typography>
      </Sheet>
    );
  }

  const groupedChats = data.chats.reduce((groups: GroupedChats, chat) => {
    const lastMessageDate = new Date(chat.last_message_time);
    const now = new Date();
    const diffInDays =
      (now.getTime() - lastMessageDate.getTime()) / (1000 * 60 * 60 * 24);

    let group = t("chats.groups.aWhileAgo");
    if (diffInDays < 1) group = t("chats.groups.today");
    else if (diffInDays < 2) group = t("chats.groups.yesterday");
    else if (diffInDays < 7) group = t("chats.groups.thisWeek");

    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(chat);
    return groups;
  }, {});

  const groupOrder = [
    t("chats.groups.today"),
    t("chats.groups.yesterday"),
    t("chats.groups.thisWeek"),
    t("chats.groups.aWhileAgo"),
  ];

  return (
    <List
      sx={{
        "--ListItem-radius": "4px",
        "--ListItem-minHeight": "40px",
        "--ListItemDecorator-size": "32px",
        gap: 0.25,
        p: 0.25,
      }}
    >
      {groupOrder.map((groupName) => {
        const chats = groupedChats[groupName];
        if (!chats?.length) return null;

        return (
          <div key={groupName}>
            <ListItem
              sx={{
                p: 0.5,
                ml: -1.5,
              }}
            >
              <Typography
                level="body-xs"
                sx={{
                  color: "neutral.500",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                }}
              >
                {groupName}
              </Typography>
            </ListItem>
            {chats.map((chat) => (
              <ListItem
                key={chat.id}
                sx={{
                  p: 0.4,
                  mb: 1,
                }}
              >
                <ListItemButton
                  sx={{
                    p: 0.1,
                    bgcolor: "transparent",
                    borderRadius: "var(--ListItem-radius)",
                    display: "flex",
                    alignItems: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "var(--joy-palette-background-level2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                  onClick={() => {
                    closeSidebar();
                    // Optimistically update current conversation so mobile header shows immediately
                    setConversation({
                      characterName: chat.character_name,
                      avatarUrl: chat.avatar_url,
                      characterId: chat.character_id,
                    });
                    navigate(`/character/${chat.character_id}`, {
                      state: {
                        conversationId: chat.id,
                        name: chat.character_name,
                        avatarUrl: chat.avatar_url,
                      },
                    });
                  }}
                >
                  <CharacterAvatar
                    src={chat.avatar_url}
                    name={chat.character_name}
                    sx={{
                      mr: -0.5,
                      ml: 0.5,
                      width: 32,
                      height: 32,
                      flexShrink: 0,
                    }}
                  />

                  <ListItemContent
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Typography
                      level="title-sm"
                      sx={{
                        m: 0,
                        lineHeight: 1.4,
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        color: {
                          xs: "neutral.700",
                          [`[data-joy-color-scheme="dark"] &`]: {
                            color: "#aaaaaa",
                          },
                        },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "160px",
                      }}
                    >
                      {chat.character_name}
                    </Typography>
                  </ListItemContent>
                </ListItemButton>
              </ListItem>
            ))}
          </div>
        );
      })}
    </List>
  );
}
